/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import { object } from 'prop-types';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useDispatch } from 'react-redux';

import getStatusCSS from '../helper';
import ResponsiveImage from '../../../../../components/GraphqlComponents/GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import { secureProtocol } from '../../../utils/secureProtocol';
import SubscriptionSwap from '../SwapSubscription/SwapModal';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import pastEndDate from '../../Subscriptions/Partials/CommonUtils';

const useStyles = makeStyles({
    container: {
        width: '100%',
        maxWidth: '295px',
        marginRight: '20px',
        '& div, & p, & h1, & h2, & h3': {
            fontFamily: 'Lato, sans-serif',
        },
        '@media screen and (max-width: 1024px)': {
            maxWidth: '100%',
            marginRight: '0px',
            display: 'flex',
            paddingBottom: '24px',
        },
        '@media screen and (max-width: 424px)': {
            flexDirection: 'column',
        },
    },
    image: {
        minWidth: '169px',
        maxWidth: '295px',
        width: '100%',
        '@media screen and (max-width: 540px)': {
            maxWidth: '200px',
            minWidth: '200px',
        },
        '@media screen and (max-width: 480px)': {
            maxWidth: '180px',
            minWidth: '180px',
        },
        '@media screen and (max-width: 424px)': {
            maxWidth: '100%',
        },
        '& img': {
            width: '100%',
            aspectRatio: '0.915',
        },
    },
    canceledImage: {
        opacity: 0.7,
    },
    details: {
        fontWeight: '700',
        fontSize: '20px',
        lineHeight: '32px',
        letterSpacing: '0.01em',
        color: '#000',
        '@media screen and (max-width: 1024px)': {
            lineHeight: '20px',
            paddingLeft: '16px',
            fontSize: '14px',
            width: '100%',
        },
        '@media screen and (max-width: 424px)': {
            paddingLeft: '0px',
            paddingTop: '16px',
        },
        '& ._name': {
            margin: '24px 0',
            '@media screen and (max-width: 1024px)': {
                fontSize: '18px !important',
                margin: '0 0 24px',
                lineHeight: '26px',
                letterSpacing: '0.18px',
            },
            '@media screen and (max-width: 424px)': {
                fontSize: '16px !important',
                lineHeight: '24px',
                letterSpacing: '0.16px',
            },
        },
        '& .pausedStatus': {
            color: '#595959',
        },
        '& .activeStatus': {
            color: '#1D773F',
        },
        '& .canceledStatus': {
            color: '#a1001a',
        },
        '& .MuiButton-root': {
            marginTop: '30px',
            background: '#53237F',
            borderRadius: '3px',
            width: '100%',
            maxWidth: '300px',
            padding: '11px 28px',
            gap: '10px',
            color: 'white',
            fontSize: '20px',
            letterSpacing: '0.04em',
            lineHeight: '26px',
            boxShadow: 'none',
            textTransform: 'capitalize',
            '@media screen and (max-width: 1024px)': {
                letterSpacing: '0.64px',
                fontSize: '16px',
                lineHeight: '24px',
                padding: '12px 20px',
            },
            '@media screen and (max-width: 424px)': {
                maxWidth: '100%',
            },
            '@media screen and (min-width: 424px) and (max-width: 480px)': {
                padding: '12px 8px',
            },
        },
    },
});

const ProductDetails = ({ data, featureFlags }) => {
    const dispatch = useDispatch();
    const classes = useStyles(data);
    const [showVariants, setShowVariants] = useState(false);
    const { subscriptionStatus, subscriptionStatusClass } = getStatusCSS(data);
    const fallbackImgURL = 'https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/bltfc354cd0e7f66f1b/6267900f6b305b67539338d2/image_not_available.png';
    const productImage = data?.productImageCategory ? secureProtocol(data?.productImageCategory) : fallbackImgURL;

    // AB testing (LD) and verifying subscription status
    const enableSwapSubscription = featureFlags['enable-swap-subscription'] && featureFlags['enable-sup-swap-ab-test'] && !pastEndDate(data) && data.subscriptionStatus !== 'CANCELLED';

    const changeProduct = () => {
        dispatch(trackEvent({
            eventCategory: 'Subscriptions Management Portal',
            eventAction: 'click - Change Product',
            eventLabel: 'Change Product Selected',
        }));
        setShowVariants((prev) => !prev);
    };

    return (
        <div className={classes.container}>
            <div className={`${data.subscriptionStatus === 'CANCELLED' ? classes.canceledImage : ''} ${classes.image}`}>
                <ResponsiveImage
                    className=""
                    path={productImage}
                    alt={data.productName}
                    params={{ desktop: 'quality=100', tablet: 'quality=100', mobile: 'quality=100' }}
                />
            </div>
            <div className={classes.details}>
                <div className="_name">{data.productName}</div>
                <div>Price: ${data.price}</div>
                <div>Status: <span className={subscriptionStatusClass}>{subscriptionStatus}</span></div>
                {enableSwapSubscription && (
                    <Button variant="contained" onClick={changeProduct}>
                        Change Product
                    </Button>
                )}
            </div>
            {showVariants
                && (
                    <SubscriptionSwap
                        subscription={data}
                        openModal={showVariants}
                        productPrice={data.price}
                        fallbackImgURL={fallbackImgURL}
                        toggleModal={() => setShowVariants((prev) => !prev)}
                    />
                )}
        </div>
    );
};

ProductDetails.propTypes = {
    data: object.isRequired,
    featureFlags: object.isRequired,
};

export default ProductDetails;
